// Tablero de campañas (Marketing M1-4): reporte de Meta, indicadores, semáforo, meta del mes y propuestas.
// Funciones puras, con pruebas. Los números de Meta llegan en un archivo que Valentina descarga del Administrador de
// anuncios; los pedidos y las ventas salen del sistema.
import { addDias } from './fechas';
import { money } from './dinero';
import { anuncioEnTexto, diasCampana, limpiarCodigo, nombreEnMeta, presupuestoDiario, type Anuncio, type Campana } from './campanas';
import { fechaDesde, normCol, valorDesde } from './importar';
import { cuenta, fecha, totalesPedido, type Pedido } from './pedidos';
import type { CeldaLeida, HojaLeida } from './xlsx';

const cop = (v: number) => money(v, 'COP');
const pctTxt = (x: number) => (x * 100).toLocaleString('es-CO', { maximumFractionDigits: 1 }) + ' %';
export const veces = (x: number) => x.toLocaleString('es-CO', { maximumFractionDigits: 1 }) + '×';
const MESES_L = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const nombreMes = (mes: string) => MESES_L[+mes.slice(5, 7) - 1] || mes;

// ---------- Reporte de Meta ----------

export type CampoMeta = 'anuncio' | 'campana' | 'dia' | 'desde' | 'hasta' | 'gasto' | 'conversaciones' | 'impresiones' | 'alcance' | 'clics';
export const CAMPOS_META: { id: CampoMeta; label: string }[] = [
  { id: 'anuncio', label: 'Nombre del anuncio' }, { id: 'campana', label: 'Nombre de la campaña' }, { id: 'gasto', label: 'Importe gastado' },
  { id: 'conversaciones', label: 'Conversaciones iniciadas' }, { id: 'dia', label: 'Día' }, { id: 'desde', label: 'Inicio del informe' },
  { id: 'hasta', label: 'Fin del informe' }, { id: 'impresiones', label: 'Impresiones' }, { id: 'alcance', label: 'Alcance' }, { id: 'clics', label: 'Clics en el enlace' },
];
// Encabezados del reporte en español y en inglés, en orden de preferencia (las conversaciones explícitas antes que
// "Resultados", que puede medir otra cosa). *Supuesto: los nombres de Meta pueden variar; si no se reconocen, se
// eligen a mano.*
const ALIAS_META: Record<CampoMeta, string[]> = {
  anuncio: ['nombre del anuncio', 'nombre de anuncio', 'anuncio', 'ad name'],
  campana: ['nombre de la campana', 'nombre de campana', 'campana', 'campaign name'],
  gasto: ['importe gastado', 'amount spent', 'gasto', 'importe gastado total'],
  conversaciones: ['conversaciones con mensajes iniciadas', 'conversaciones de mensajes iniciadas', 'conversaciones iniciadas', 'messaging conversations started', 'resultados', 'results'],
  dia: ['dia', 'day'],
  desde: ['inicio del informe', 'reporting starts', 'inicio'],
  hasta: ['fin del informe', 'reporting ends', 'fin'],
  impresiones: ['impresiones', 'impressions'],
  alcance: ['alcance', 'reach'],
  clics: ['clics en el enlace', 'link clicks', 'clics todos', 'clics'],
};
export type ColumnasMeta = Partial<Record<CampoMeta, number>>;

// Por cada dato, el primer alias que aparezca: igual al encabezado o al comienzo ("Importe gastado (COP)").
export function detectarColumnasMeta(encabezados: CeldaLeida[]): ColumnasMeta {
  const hs = encabezados.map(normCol), out: ColumnasMeta = {}, usadas = new Set<number>();
  for (const k of Object.keys(ALIAS_META) as CampoMeta[]) {
    for (const a of ALIAS_META[k]) {
      let i = hs.findIndex((h, j) => !usadas.has(j) && h === a);
      if (i < 0) i = hs.findIndex((h, j) => !usadas.has(j) && h.startsWith(a + ' '));
      if (i >= 0) { out[k] = i; usadas.add(i); break; }
    }
  }
  return out;
}
export const reporteListo = (c: ColumnasMeta) => c.gasto != null && (c.anuncio != null || c.campana != null);
export function encabezadoMeta(h: HojaLeida): number {
  for (let i = 0; i < Math.min(10, h.filas.length); i++) if (reporteListo(detectarColumnasMeta(h.filas[i] || []))) return i;
  return -1;
}
// Moneda del gasto según su encabezado: "Importe gastado (USD)". Sin moneda, la de la cuenta: pesos.
export function monedaGasto(encabezado: CeldaLeida): string {
  const m = normCol(encabezado).match(/\b(cop|usd|eur|mxn|dop)\b/);
  return m ? m[1].toUpperCase() : 'COP';
}

// A qué se asigna una fila: un anuncio, la campaña sin anuncio, o nada (no se guarda).
export type Destino = { pauta_id: number; anuncio_id: number | null } | null;
export interface FilaMeta {
  donde: string; nombre: string; campanaMeta: string; desde: string; hasta: string;
  gasto: number; impresiones: number; alcance: number; clics: number; conversaciones: number;
  destino: Destino; como: 'codigo' | 'campana' | 'sin';
}
export interface AnalisisMeta { filas: FilaMeta[]; errores: { donde: string; motivo: string }[]; omitidas: number; moneda: string }

// El anuncio por su código (al comienzo del nombre, como lo da el sistema, o en cualquier parte); si no, la campaña por
// su código o su nombre.
export function destinoDe(nombre: string, campanaMeta: string, campanas: Campana[]): { destino: Destino; como: FilaMeta['como'] } {
  const primero = (s: string) => limpiarCodigo(s.trim().split(/\s+/)[0] || '');
  const cod = primero(nombre);
  for (const c of campanas) { const a = c.anuncios.find(x => x.codigo === cod); if (a) return { destino: { pauta_id: c.id, anuncio_id: a.id }, como: 'codigo' }; }
  const a = nombre ? anuncioEnTexto(nombre, campanas) : null;
  if (a) return { destino: { pauta_id: a.campana_id, anuncio_id: a.id }, como: 'codigo' };
  const cc = primero(campanaMeta), n = normCol(campanaMeta);
  const c = campanas.find(k => k.codigo === cc) || campanas.find(k => !!n && normCol(k.nombre) === n) || campanas.find(k => !!n && n.includes(normCol(`${k.codigo} ${k.nombre}`)));
  return c ? { destino: { pauta_id: c.id, anuncio_id: null }, como: 'campana' } : { destino: null, como: 'sin' };
}

const entero = (v: CeldaLeida) => Math.max(0, Math.round(valorDesde(v)));
export function analizarMeta(hoja: HojaLeida, encabezado: number, col: ColumnasMeta, campanas: Campana[], op: { desde?: string; hasta?: string; tasa: number }): AnalisisMeta {
  const filas: FilaMeta[] = [], errores: AnalisisMeta['errores'] = [];
  let omitidas = 0;
  const moneda = col.gasto != null ? monedaGasto(hoja.filas[encabezado]?.[col.gasto]) : 'COP';
  for (let i = encabezado + 1; i < hoja.filas.length; i++) {
    const r = hoja.filas[i] || [], val = (k: CampoMeta) => (col[k] != null ? r[col[k]!] ?? null : null);
    if (r.every(x => x == null || String(x).trim() === '')) continue;
    const donde = `Fila ${i + 1}`, nombre = String(val('anuncio') ?? '').replace(/\s+/g, ' ').trim(), campanaMeta = String(val('campana') ?? '').replace(/\s+/g, ' ').trim();
    const gasto = Math.max(0, valorDesde(val('gasto'))) * (moneda === 'COP' ? 1 : op.tasa), conversaciones = entero(val('conversaciones'));
    const impresiones = entero(val('impresiones')), alcance = entero(val('alcance')), clics = entero(val('clics'));
    if (!nombre && !campanaMeta) { omitidas++; continue; }                         // fila de totales
    if (!gasto && !impresiones && !conversaciones) { omitidas++; continue; }         // anuncio que no corrió
    let desde = '', hasta = '';
    if (col.dia != null) desde = hasta = fechaDesde(val('dia'));
    else { desde = col.desde != null ? fechaDesde(val('desde')) : op.desde || ''; hasta = col.hasta != null ? fechaDesde(val('hasta')) : op.hasta || ''; }
    if (!desde || !hasta) { errores.push({ donde, motivo: 'Sin fechas: escribe el periodo del reporte.' }); continue; }
    if (hasta < desde) { errores.push({ donde, motivo: 'La fecha final es antes de la inicial.' }); continue; }
    filas.push({ donde, nombre: nombre || campanaMeta, campanaMeta, desde, hasta, gasto: Math.round(gasto * 100) / 100, impresiones, alcance, clics, conversaciones, ...destinoDe(nombre, campanaMeta, campanas) });
  }
  return { filas, errores, omitidas, moneda };
}

// Lo que se guarda: las filas con destino, sumadas cuando el mismo anuncio viene varias veces en el mismo periodo
// (el mismo anuncio en dos conjuntos de anuncios).
export interface ResultadoMeta { pauta_id: number; anuncio_id: number | null; nombre: string; desde: string; hasta: string; gasto: number; impresiones: number; alcance: number; clics: number; conversaciones: number }
export function agruparMeta(filas: (FilaMeta & { destino: Destino })[]): ResultadoMeta[] {
  const por = new Map<string, ResultadoMeta>();
  for (const f of filas) {
    if (!f.destino) continue;
    const k = [f.destino.pauta_id, f.destino.anuncio_id ?? 'n:' + f.nombre, f.desde, f.hasta].join('|');
    const x = por.get(k);
    if (!x) { por.set(k, { ...f.destino, nombre: f.nombre, desde: f.desde, hasta: f.hasta, gasto: f.gasto, impresiones: f.impresiones, alcance: f.alcance, clics: f.clics, conversaciones: f.conversaciones }); continue; }
    x.gasto = Math.round((x.gasto + f.gasto) * 100) / 100; x.impresiones += f.impresiones; x.alcance += f.alcance; x.clics += f.clics; x.conversaciones += f.conversaciones;
  }
  return [...por.values()];
}

// ---------- Indicadores de una campaña ----------

export interface Linea { anuncio: Anuncio | null; gasto: number; conversaciones: number; impresiones: number; pedidos: number; ventas: number; margen: number; cotizaciones: number }
export const costoPorPedido = (l: Pick<Linea, 'gasto' | 'pedidos'>) => (l.pedidos ? l.gasto / l.pedidos : null);
export const costoPorConversacion = (l: Pick<Linea, 'gasto' | 'conversaciones'>) => (l.conversaciones ? l.gasto / l.conversaciones : null);
export const deConversacionAPedido = (l: Pick<Linea, 'pedidos' | 'conversaciones'>) => (l.conversaciones ? l.pedidos / l.conversaciones : null);
export const retorno = (l: Pick<Linea, 'gasto' | 'ventas'>) => (l.gasto ? l.ventas / l.gasto : null);

export interface ResumenPauta { desde: string | null; hasta: string | null; lineas: Linea[]; sinAnuncio: Linea; total: Linea; faltan: { dias: number; desde: string; hasta: string } | null }

// Gasto de Meta y pedidos del sistema por anuncio. Los pedidos se cuentan hasta el último día que trae el reporte,
// para comparar el mismo periodo. Sin reporte, se cuentan todos.
export function resumenPauta(c: Campana, res: ResultadoMeta[], pedidos: Pedido[]): ResumenPauta {
  const mios = res.filter(r => r.pauta_id === c.id);
  const desde = mios.length ? mios.reduce((m, r) => (r.desde < m ? r.desde : m), mios[0].desde) : null;
  const hasta = mios.length ? mios.reduce((m, r) => (r.hasta > m ? r.hasta : m), mios[0].hasta) : null;
  const vacia = (anuncio: Anuncio | null): Linea => ({ anuncio, gasto: 0, conversaciones: 0, impresiones: 0, pedidos: 0, ventas: 0, margen: 0, cotizaciones: 0 });
  const lineas = c.anuncios.map(a => vacia(a)), sinAnuncio = vacia(null), total = vacia(null);
  const de = (anuncio_id: number | null | undefined) => (anuncio_id != null && lineas.find(l => l.anuncio!.id === anuncio_id)) || sinAnuncio;
  for (const r of mios) for (const l of [de(r.anuncio_id), total]) { l.gasto += +r.gasto; l.conversaciones += r.conversaciones; l.impresiones += r.impresiones; }
  for (const p of pedidos) {
    if (p.pauta_id !== c.id || (hasta && p.fecha > hasta)) continue;
    for (const l of [de(p.anuncio_id), total]) {
      if (p.estado === 'cotizacion') l.cotizaciones++;
      else if (cuenta(p)) { const t = totalesPedido(p); l.pedidos++; l.ventas += t.total * t.tasa; l.margen += t.margen; }
    }
  }
  return { desde, hasta, lineas, sinAnuncio, total, faltan: diasSinDatos(c, mios, hasta) };
}

// Días de la campaña sin datos de Meta (hasta el último día reportado): el gasto saldría más bajo de lo real.
export function diasSinDatos(c: Pick<Campana, 'inicio'>, res: Pick<ResultadoMeta, 'desde' | 'hasta'>[], hasta: string | null) {
  if (!c.inicio || !hasta || hasta < c.inicio) return null;
  const cubiertos = new Set<string>();
  for (const r of res) for (let d = r.desde, n = 0; d <= r.hasta && n < 1000; d = addDias(d, 1), n++) cubiertos.add(d);
  let dias = 0, primero = '', ultimo = '';
  for (let d = c.inicio, n = 0; d <= hasta && n < 1000; d = addDias(d, 1), n++) {
    if (cubiertos.has(d)) continue;
    dias++; if (!primero) primero = d; if (!ultimo || addDias(ultimo, 1) === d) ultimo = d;
  }
  return dias ? { dias, desde: primero, hasta: ultimo } : null;
}

// Semáforo del costo por pedido contra el tope de la campaña: verde hasta el tope, amarillo hasta 1,3 veces,
// rojo por encima, o si un anuncio gastó 3 veces el tope sin un pedido.
export type Tono = 'ok' | 'warn' | 'bad' | '';
export function semaforo(gasto: number, pedidos: number, tope: number | null): { tono: Tono; label: string } {
  if (!tope) return { tono: '', label: 'Sin tope' };
  if (!pedidos) return gasto >= 3 * tope ? { tono: 'bad', label: 'Sin pedidos' } : { tono: '', label: gasto ? 'Aún sin pedidos' : 'Sin gasto' };
  const cpp = gasto / pedidos;
  return cpp <= tope ? { tono: 'ok', label: 'Bien' } : cpp <= 1.3 * tope ? { tono: 'warn', label: 'Vigilar' } : { tono: 'bad', label: 'Caro' };
}

// Ritmo del gasto: lo gastado contra el plan (presupuesto repartido parejo) hasta el último día reportado.
export function ritmoGasto(c: Pick<Campana, 'inicio' | 'fin' | 'presupuesto'>, gasto: number, hasta: string | null) {
  if (!c.inicio || !c.fin || !c.presupuesto || !hasta || hasta < c.inicio) return null;
  const dias = diasCampana({ inicio: c.inicio, fin: hasta < c.fin ? hasta : c.fin });
  const plan = c.presupuesto * dias / diasCampana(c), ratio = plan ? gasto / plan : 0;
  const [tono, label]: [Tono, string] = ratio >= 0.9 && ratio <= 1.1 ? ['ok', 'Gasto al día'] : ratio < 0.9 ? [ratio >= 0.75 ? 'warn' : 'bad', 'Gasta menos de lo planeado']
    : [ratio <= 1.25 ? 'warn' : 'bad', 'Gasta más rápido de lo planeado'];
  return { plan, ratio, dias, tono, label, diario: presupuestoDiario(c) };
}

// ---------- Meta de pedidos del mes ----------

export interface MetaPedidos { base: number; meses: Record<string, number> }
export const META_VACIA: MetaPedidos = { base: 40, meses: {} };
export const metaDelMes = (m: MetaPedidos, mes: string) => m.meses[mes] ?? m.base;
// Pedidos del mes hasta hoy (los que cuentan como venta), de dónde vinieron y si va al ritmo de la meta.
export function avanceMes(pedidos: Pick<Pedido, 'fecha' | 'estado' | 'pauta_id' | 'mensajes_id'>[], meta: number, dia: string) {
  const mes = dia.slice(0, 7), diasMes = new Date(+mes.slice(0, 4), +mes.slice(5, 7), 0).getDate(), d = +dia.slice(8, 10);
  const del = pedidos.filter(p => cuenta(p) && p.fecha.slice(0, 7) === mes && p.fecha <= dia);
  const pauta = del.filter(p => p.pauta_id).length, mensajes = del.filter(p => !p.pauta_id && p.mensajes_id).length, llevan = del.length;
  const esperado = meta * d / diasMes, proyeccion = Math.round(llevan / d * diasMes);
  const [tono, label]: [Tono, string] = llevan >= Math.floor(esperado) ? ['ok', 'En ritmo'] : llevan >= 0.9 * esperado ? ['warn', 'Un poco atrás'] : ['bad', 'Atrasado'];
  return { mes, meta, llevan, pauta, mensajes, otros: llevan - pauta - mensajes, esperado, proyeccion, tono, label, diasMes, dia: d };
}

// ---------- Gráfico semanal ----------

// Semanas de lunes a domingo desde el inicio de la campaña. Un periodo de varios días reparte su gasto parejo entre
// sus días (si el reporte trae cada día, el reparto es exacto).
export const lunesDe = (iso: string) => { const w = new Date(iso + 'T12:00:00').getDay(); return addDias(iso, -((w + 6) % 7)); };
export function semanas(c: Pick<Campana, 'id' | 'inicio' | 'fin'>, res: ResultadoMeta[], pedidos: Pedido[], corte: string) {
  const mios = res.filter(x => x.pauta_id === c.id), inicio = c.inicio || mios.reduce((m, r) => (!m || r.desde < m ? r.desde : m), '');
  if (!inicio || corte < inicio) return [];
  const fin = c.fin && c.fin < corte ? c.fin : corte, out: { desde: string; hasta: string; gasto: number; pedidos: number }[] = [];
  for (let l = lunesDe(inicio), n = 0; l <= fin && n < 60; l = addDias(l, 7), n++) out.push({ desde: l < inicio ? inicio : l, hasta: addDias(l, 6) > fin ? fin : addDias(l, 6), gasto: 0, pedidos: 0 });
  const semana = (d: string) => out.find(s => d >= s.desde && d <= s.hasta);
  for (const r of mios) {
    const n = diasCampana({ inicio: r.desde, fin: r.hasta }) || 1;
    for (let d = r.desde, i = 0; d <= r.hasta && i < 1000; d = addDias(d, 1), i++) { const s = semana(d); if (s) s.gasto += +r.gasto / n; }
  }
  for (const p of pedidos) if (p.pauta_id === c.id && cuenta(p)) { const s = semana(p.fecha); if (s) s.pedidos++; }
  return out;
}

// ---------- Propuestas ----------

export type Donde = 'sistema' | 'meta' | 'whatsapp';
export interface Propuesta { clave: string; pauta_id: number | null; titulo: string; porque: string; como: string; donde: Donde }
export interface DatosPropuestas {
  c: Campana; r: ResumenPauta; ritmo: ReturnType<typeof ritmoGasto>; semana: string;
  mes: ReturnType<typeof avanceMes>; mensajes: { id: string; nombre: string; pendientes: number; total: number }[];
}
// El sistema propone; Valentina aprueba o descarta. La clave (regla, anuncio y semana) evita repetir lo ya decidido
// en la misma semana.
export function proponer({ c, r, ritmo, semana, mes, mensajes }: DatosPropuestas): Propuesta[] {
  const out: Propuesta[] = [], tope = c.tope_cpp, t = r.total, s = semana;
  const add = (clave: string, p: Omit<Propuesta, 'clave' | 'pauta_id'>, global = false) => out.push({ clave, pauta_id: global ? null : c.id, ...p });
  if (r.faltan) add(`faltan-${c.codigo}-${s}`, { donde: 'meta', titulo: 'Sube el reporte de Meta que falta',
    porque: `No hay datos de Meta de ${r.faltan.dias} ${r.faltan.dias === 1 ? 'día' : 'días'} de la campaña (desde el ${fecha(r.faltan.desde)}): el gasto y el costo por pedido salen más bajos de lo real.`,
    como: 'En Meta: descarga el reporte de esos días y súbelo aquí.' });
  if (!tope && t.gasto > 0) add(`tope-${c.codigo}`, { donde: 'sistema', titulo: 'Define el tope de costo por pedido',
    porque: 'Sin tope, el semáforo no puede decir qué anuncio está caro.' + (t.pedidos && t.margen > 0 ? ` El margen bruto promedio por pedido de esta campaña es ${cop(t.margen / t.pedidos)}: si la pauta cuesta más que eso por pedido, se pierde plata.` : ''),
    como: 'En el sistema: la campaña → Editar datos → Tope de costo por pedido.' });
  if (tope) {
    const activas = r.lineas.filter(l => l.anuncio!.activo);
    for (const l of activas) {
      const a = l.anuncio!;
      if (!l.pedidos && l.gasto >= 3 * tope) add(`pausar-${a.codigo}-${s}`, { donde: 'meta', titulo: `Pausar ${a.codigo} · ${a.nombre}`,
        porque: `Lleva ${cop(l.gasto)}${l.conversaciones ? ` y ${l.conversaciones} conversaciones` : ''} sin un solo pedido.`,
        como: `En Meta: apaga el anuncio "${nombreEnMeta(a)}". Luego, aquí, desactívalo en el paso 2 de la campaña.` });
    }
    for (const l of activas) {
      const a = l.anuncio!, cpp = costoPorPedido(l);
      if (cpp != null && cpp > 1.3 * tope) {
        const cpcA = costoPorConversacion(l), cpcT = costoPorConversacion(t), tasaA = deConversacionAPedido(l), tasaT = deConversacionAPedido(t);
        if (cpcA != null && cpcT != null && tasaA != null && tasaT != null && cpcA <= 1.2 * cpcT)
          add(`chat-${a.codigo}-${s}`, { donde: 'whatsapp', titulo: `Revisar cómo se responde a quien escribe por ${a.codigo} · ${a.nombre}`,
            porque: `Cada conversación cuesta ${cop(cpcA)}, parecido al promedio (${cop(cpcT)}), pero solo ${pctTxt(tasaA)} termina en pedido (promedio ${pctTxt(tasaT)}). El anuncio atrae; la venta se cae en el chat o en el precio.`,
            como: `Leer 10 chats que llegaron con el código ${a.codigo} y ajustar la respuesta antes de cambiar la pieza.` });
        else add(`pieza-${a.codigo}-${s}`, { donde: 'meta', titulo: `Cambiar la pieza o el público de ${a.codigo} · ${a.nombre}`,
          porque: `Cada pedido cuesta ${cop(cpp)}, más que el tope de ${cop(tope)}${cpcA != null && cpcT != null ? `, y cada conversación ${cop(cpcA)} (promedio ${cop(cpcT)}): el anuncio atrae poco` : ''}.`,
          como: 'Crea aquí el anuncio nuevo para que tenga su código; en Meta, prueba otra pieza o ajusta el público.' });
      }
    }
  }
  const conGasto = r.lineas.filter(l => l.gasto > 0);
  const buenos = conGasto.filter(l => l.pedidos >= 3 && (!tope || l.gasto / l.pedidos <= tope)).sort((x, y) => x.gasto / x.pedidos - y.gasto / y.pedidos);
  if (conGasto.length >= 2 && buenos.length) {
    const l = buenos[0], a = l.anuncio!;
    add(`mejor-${a.codigo}-${s}`, { donde: 'meta', titulo: `Probar ${a.codigo} · ${a.nombre} con clientes anteriores`,
      porque: `Es el anuncio con menor costo por pedido (${cop(l.gasto / l.pedidos)})${l.gasto ? ` y retorno de ${veces(l.ventas / l.gasto)}` : ''}.`,
      como: 'En el sistema: Clientes → Audiencia para Meta. En Meta: sube la lista como público personalizado y duplica el anuncio para ese público.' });
  }
  if (ritmo && ritmo.tono !== 'ok' && !r.faltan) {
    if (ritmo.ratio < 0.9) add(`ritmo-bajo-${c.codigo}-${s}`, { donde: 'meta', titulo: 'Meta está gastando menos de lo planeado',
      porque: `Lleva ${cop(t.gasto)} de ${cop(ritmo.plan)} planeados a esta fecha (${pctTxt(ritmo.ratio)}).`,
      como: `En Meta: revisa que la campaña y sus anuncios estén activos y que el presupuesto diario sea ${cop(ritmo.diario)}. Un público muy pequeño también frena el gasto.` });
    else add(`ritmo-alto-${c.codigo}-${s}`, { donde: 'meta', titulo: 'Meta está gastando más rápido de lo planeado',
      porque: `Lleva ${cop(t.gasto)} de ${cop(ritmo.plan)} planeados a esta fecha (${pctTxt(ritmo.ratio)}): a este ritmo el presupuesto se acaba antes del ${c.fin ? fecha(c.fin) : 'final'}.`,
      como: `En Meta: revisa el presupuesto diario: debería ser ${cop(ritmo.diario)}.` });
  }
  if (mes.tono === 'bad') {
    const faltan = Math.max(0, mes.meta - mes.llevan), quedan = mes.diasMes - mes.dia;
    add(`meta-mes-${mes.mes}-${s}`, { donde: 'sistema', titulo: `Vas atrasado en la meta de ${nombreMes(mes.mes)}`,
      porque: `Llevas ${mes.llevan} de ${mes.meta} pedidos; a esta fecha deberían ser ${Math.floor(mes.esperado)}. Faltan ${faltan} en ${quedan} ${quedan === 1 ? 'día' : 'días'}.`,
      como: 'Opciones: mensajes uno a uno a un segmento de clientes anteriores (sin costo de pauta) o subir el presupuesto en Meta, que es decisión del negocio.' }, true);
  }
  for (const m of mensajes) if (m.pendientes > 0) add(`mensajes-${m.id}-${s}`, { donde: 'whatsapp', titulo: `Terminar de enviar "${m.nombre}"`,
    porque: `Quedan ${m.pendientes} de ${m.total} clientes sin gestionar; la reactivación no tiene costo de pauta.`,
    como: 'En el sistema: Campañas y mensajes → Mensajes uno a uno → la campaña → siguiente destinatario.' }, true);
  return out;
}
